/**
 * Item Types Page
 *
 * A 5×6 grid showing all possible item type combinations (altitude × category).
 * Items are displayed as draggable chips — drag to a new cell to change
 * the item's category and/or altitude.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, AlertTriangle, GripVertical, Info, X, List, Tag, Repeat2, ChevronDown } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { InfoModal, type InfoSection } from '../../components/prioritization/InfoModal';
import {
  DndContext,
  DragOverlay,
  useDroppable,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useItemCategoriesStore } from '@/stores/prioritization/itemCategoriesStore';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { CATEGORY_BADGE, resolveCategoryHexMap } from '@/lib/itemCategoryColors';
import type { ItemLanguageType, Item, ItemFrame } from '@/types/prioritization/lifecycle';
import { useVisualViewportFlags } from '@/hooks/prioritization/useVisualViewport';
import { effortFieldsFromAltitudeLevel } from '@/lib/itemToPlanningAdapter';
import { getItemEffortBandHours } from '@/stores/prioritization/planningDefaultsStore';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const CATEGORIES: ItemLanguageType[] = [
  'problem',
  'opportunity',
  'risk',
  'solution',
  'commitment',
  'investment',
];

const ALTITUDES = [5, 4, 3, 2, 1] as const;

const TYPE_NAMES: Record<ItemLanguageType, Record<number, string>> = {
  problem:    { 5: 'Root Cause', 4: 'Pattern',      3: 'Gap',         2: 'Defect',      1: 'Bug'        },
  opportunity:{ 5: 'Strategy',   4: 'Program',      3: 'Initiative',  2: 'Prospect',    1: 'Quick Win'  },
  risk:       { 5: 'Crisis',     4: 'Threat',       3: 'Exposure',    2: 'Concern',     1: 'Flag'       },
  solution:   { 5: 'Platform',   4: 'Initiative',   3: 'Epic',        2: 'Story',       1: 'Task'       },
  commitment: { 5: 'Mandate',    4: 'Pledge',       3: 'Contract',    2: 'Deliverable', 1: 'Action'     },
  investment: { 5: 'Transformation', 4: 'Portfolio', 3: 'Project',    2: 'Pilot',       1: 'Experiment' },
};

const CATEGORY_LABEL: Record<ItemLanguageType, string> = {
  problem:    'Problem',
  opportunity:'Opportunity',
  risk:       'Risk',
  solution:   'Solution',
  commitment: 'Commitment',
  investment: 'Investment',
};

/** Separator used in droppable cell IDs to avoid ambiguity with category names */
const CELL_SEP = '__';

function cellId(cat: ItemLanguageType, alt: number) {
  return `${cat}${CELL_SEP}${alt}`;
}

function parseCellId(id: string): { cat: ItemLanguageType; alt: number } | null {
  const idx = id.lastIndexOf(CELL_SEP);
  if (idx === -1) return null;
  const cat = id.slice(0, idx) as ItemLanguageType;
  const alt = Number(id.slice(idx + CELL_SEP.length));
  if (!ALTITUDES.includes(alt as typeof ALTITUDES[number])) return null;
  return { cat, alt };
}

// ---------------------------------------------------------------------------
// DraggableItem
// ---------------------------------------------------------------------------

function DraggableItem({ item, category }: { item: Item; category: ItemLanguageType }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.id });
  const navigate = useNavigate();
  const colors = CATEGORY_BADGE[category] ?? { bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      title="Click to open · Drag to reclassify"
      onClick={() => navigate(`/prioritization/items/${item.id}`)}
      className={`flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-grab active:cursor-grabbing select-none transition-opacity
        ${colors.bg} ${colors.text} ${colors.border}
        ${isDragging ? 'opacity-30' : 'hover:opacity-80 hover:underline'}`}
    >
      <GripVertical className="h-2.5 w-2.5 opacity-40 flex-shrink-0" />
      <span className="truncate">{item.title || 'Untitled'}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverflowPopover — portal-rendered panel for "+N more" items
// ---------------------------------------------------------------------------

interface OverflowState {
  anchorRect: DOMRect;
  items: Item[];
  totalCount: number;
  category: ItemLanguageType;
  altitude: number;
  typeName: string;
}

function OverflowPopover({
  state,
  onClose,
}: {
  state: OverflowState;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const colors = CATEGORY_BADGE[state.category] ?? { bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' };

  // Position: prefer below the anchor, flip above when near bottom of viewport
  const POPOVER_HEIGHT_ESTIMATE = Math.min(state.items.length * 32 + 64, 320);
  const spaceBelow = window.innerHeight - state.anchorRect.bottom;
  const showAbove = spaceBelow < POPOVER_HEIGHT_ESTIMATE + 12 && state.anchorRect.top > POPOVER_HEIGHT_ESTIMATE + 12;

  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(state.anchorRect.left, window.innerWidth - 244),
    width: 240,
    zIndex: 9999,
    ...(showAbove
      ? { bottom: window.innerHeight - state.anchorRect.top + 4 }
      : { top: state.anchorRect.bottom + 4 }),
  };

  // Dismiss on outside click or Escape
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  // Cap visible items in popover — link out for large cells
  const MAX_IN_POPOVER = 8;
  const visibleInPopover = state.items.slice(0, MAX_IN_POPOVER);
  const hiddenCount = state.items.length - MAX_IN_POPOVER;

  return createPortal(
    <div
      ref={popoverRef}
      style={style}
      className={` border shadow-xl overflow-hidden ${colors.border}`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 ${colors.bg} border-b ${colors.border}`}>
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${colors.text}`}>
          {state.typeName} · {state.totalCount} item{state.totalCount !== 1 ? 's' : ''}
        </span>
        <button
          type="button"
          onClick={onClose}
          className={`${colors.text} opacity-60 hover:opacity-100 transition-opacity`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Scrollable item list — capped at MAX_IN_POPOVER */}
      <div className="bg-[var(--bg-raised)] py-1.5 px-2 space-y-0.5">
        {visibleInPopover.map(item => (
          <Link
            key={item.id}
            to={`/prioritization/items/${item.id}`}
            onClick={onClose}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-colors
              hover:${colors.bg} ${colors.text} group`}
          >
            <span className="truncate flex-1">{item.title || 'Untitled'}</span>
            <span className="text-[10px] opacity-40 group-hover:opacity-70 flex-shrink-0">→</span>
          </Link>
        ))}
      </div>

      {/* View all link — shown when cell is too large for the popover */}
      {hiddenCount > 0 && (
        <div className={`border-t ${colors.border} px-3 py-2 ${colors.bg}`}>
          <Link
            to={`/prioritization/items/track-cell/${state.category}/${state.altitude}`}
            onClick={onClose}
            className={`text-[11px] font-semibold ${colors.text} hover:underline flex items-center gap-1`}
          >
            View all {state.totalCount} →
          </Link>
        </div>
      )}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// DroppableCell
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// FrameChip — non-draggable chip for alternate frame entries
// ---------------------------------------------------------------------------

function FrameChip({ item, frame }: { item: Item; frame: ItemFrame }) {
  const navigate = useNavigate();
  return (
    <div
      title="Additional category — click to open item"
      onClick={() => navigate(`/prioritization/items/${item.id}`)}
      className="flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer select-none transition-opacity bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] hover:opacity-80"
    >
      <Repeat2 className="h-2.5 w-2.5 opacity-60 flex-shrink-0" />
      <span className="truncate">{frame.title || item.title || 'Untitled'}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DroppableCell
// ---------------------------------------------------------------------------

function DroppableCell({
  id,
  category,
  altitude,
  cellItems,
  frameItems,
  isOver,
  typeName,
  showItems,
  showAlternateFrames,
  onShowMore,
}: {
  id: string;
  category: ItemLanguageType;
  altitude: number;
  cellItems: Item[];
  frameItems: Array<{ item: Item; frame: ItemFrame }>;
  isOver: boolean;
  typeName: string;
  showItems: boolean;
  showAlternateFrames: boolean;
  onShowMore: (rect: DOMRect, overflowItems: Item[], totalCount: number, category: ItemLanguageType, altitude: number, typeName: string) => void;
}) {
  const { setNodeRef } = useDroppable({ id });
  const colors = CATEGORY_BADGE[category] ?? { bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' };
  const MAX_VISIBLE = 4;
  const visible = cellItems.slice(0, MAX_VISIBLE);
  const overflow = cellItems.length - MAX_VISIBLE;
  const hasItems = cellItems.length > 0;
  const hasFrameItems = frameItems.length > 0;

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[72px] p-2  transition-all space-y-1
        ${isOver ? `ring-2 ring-offset-1 ${colors.border} ${colors.bg}` : (hasItems && showItems) ? colors.bg : ''}`}
    >
      {/* Type label — always a link to the track cell items page */}
      <div className="flex items-center justify-between gap-1 mb-1">
        <Link
          to={`/prioritization/items/track-cell/${category}/${altitude}`}
          className={`text-[10px] font-semibold uppercase tracking-wide hover:underline transition-colors
            ${hasItems ? colors.text : 'text-[var(--text-ter)] hover:text-[var(--text-ter)]'}`}
          title={`View all ${typeName} items`}
          onClick={(e) => e.stopPropagation()}
        >
          {typeName}
        </Link>
        {hasItems && (
          <span className={`text-[10px] font-medium ${colors.text} opacity-50`}>
            {cellItems.length}{showAlternateFrames && hasFrameItems ? `+${frameItems.length}` : ''}
          </span>
        )}
      </div>

      {/* Item chips — only shown in items mode */}
      {showItems && (
        <div className="space-y-0.5">
          {visible.map(item => (
            <DraggableItem key={item.id} item={item} category={category} />
          ))}
          {overflow > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                onShowMore(rect, cellItems.slice(MAX_VISIBLE), cellItems.length, category, altitude, typeName);
              }}
              className={`text-[10px] px-2 py-0.5 rounded transition-colors cursor-pointer
                ${colors.text} opacity-60 hover:opacity-100 hover:${colors.bg} border border-transparent hover:${colors.border}`}
            >
              +{overflow} more
            </button>
          )}

          {/* Alternate frame chips — non-draggable, shown with a badge */}
          {showAlternateFrames && frameItems.map(({ item, frame }) => (
            <FrameChip key={`${item.id}::${frame.id}`} item={item} frame={frame} />
          ))}
        </div>
      )}

      {/* Drop hint when hovering and cell is empty */}
      {isOver && cellItems.length === 0 && (
        <div className={`text-[10px] ${colors.text} opacity-50 text-center py-1`}>Drop here</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DragOverlay preview
// ---------------------------------------------------------------------------

function ItemDragPreview({ item, category }: { item: Item; category: ItemLanguageType }) {
  const colors = CATEGORY_BADGE[category] ?? { bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' };
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded border text-xs shadow-lg rotate-1 opacity-95
      ${colors.bg} ${colors.text} ${colors.border}`}
    >
      <GripVertical className="h-2.5 w-2.5 opacity-40 flex-shrink-0" />
      <span className="max-w-[160px] truncate">{item.title || 'Untitled'}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ItemTypes() {
  const items = useItemsStore(s => s.items);
  const updateItem = useItemsStore(s => s.updateItem);

  // Load workspace categories so custom categories can provide their altitude labels
  const { categories, fetchCategories } = useItemCategoriesStore();
  const workspaceId = getCurrentWorkspaceId();
  useEffect(() => {
    if (workspaceId) fetchCategories(workspaceId);
  }, [workspaceId, fetchCategories]);

  // Build a merged TYPE_NAMES map: builtin defaults overridden by custom stored labels
  const typeNames = useMemo<Record<string, Record<number, string>>>(() => {
    const merged: Record<string, Record<number, string>> = { ...TYPE_NAMES };
    for (const cat of categories) {
      const stored = cat.altitudeLabels;
      const overrides: Record<number, string> = {};
      for (const lvl of [5, 4, 3, 2, 1] as const) {
        if (stored[lvl]) overrides[lvl] = stored[lvl]!;
      }
      if (Object.keys(overrides).length > 0) {
        merged[cat.slug] = { ...(merged[cat.slug] ?? {}), ...overrides };
      }
    }
    return merged;
  }, [categories]);

  // Derive displayable categories: builtins in order, then active custom ones
  const displayCategories = useMemo<string[]>(() => {
    const customSlugs = categories
      .filter((c) => !c.isBuiltin && c.isActive)
      .map((c) => c.slug);
    return [...CATEGORIES, ...customSlugs.filter((s) => !CATEGORIES.includes(s as ItemLanguageType))];
  }, [categories]);

  // Build category label map (builtins use CATEGORY_LABEL, custom use their stored label)
  const categoryLabelMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = { ...CATEGORY_LABEL };
    for (const cat of categories) {
      if (!map[cat.slug]) map[cat.slug] = cat.label;
    }
    return map;
  }, [categories]);

  // Build category color maps for custom categories
  const categoryBadgeMap = useMemo(() => {
    const map: Record<string, { bg: string; text: string; border: string }> = { ...CATEGORY_BADGE };
    for (const cat of categories) {
      if (!map[cat.slug] && cat.color) {
        // Use inline styles for custom — provide a neutral fallback for Tailwind classes
        map[cat.slug] = { bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' };
      }
    }
    return map;
  }, [categories]);

  const categoryHexMap = useMemo(() => {
    const map: Record<string, string> = resolveCategoryHexMap();
    for (const cat of categories) {
      if (!map[cat.slug] && cat.color) map[cat.slug] = cat.color;
    }
    return map;
  }, [categories]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [overflowPopover, setOverflowPopover] = useState<OverflowState | null>(null);
  const [showItems, setShowItems] = useState(true);
  const [showAlternateFrames, setShowAlternateFrames] = useState(false);
  const [expandedAltitude, setExpandedAltitude] = useState<number | null>(5);
  const { isPhone } = useVisualViewportFlags();

  const handleShowMore = useCallback((
    rect: DOMRect,
    overflowItems: Item[],
    totalCount: number,
    category: ItemLanguageType,
    altitude: number,
    typeName: string,
  ) => {
    setOverflowPopover({ anchorRect: rect, items: overflowItems, totalCount, category, altitude, typeName });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Group items by cell (supports all displayCategories, not just the 6 builtins)
  const cellMap = useMemo(() => {
    const map: Record<string, Item[]> = {};
    for (const cat of displayCategories) {
      for (const alt of ALTITUDES) {
        map[cellId(cat as ItemLanguageType, alt)] = [];
      }
    }
    for (const item of items) {
      const cat = item.itemCategory as string | undefined;
      const alt = item.altitudeLevel as number | undefined;
      if (cat && alt && displayCategories.includes(cat) && (ALTITUDES as readonly number[]).includes(alt as typeof ALTITUDES[number])) {
        map[cellId(cat as ItemLanguageType, alt)]?.push(item as Item);
      }
    }
    return map;
  }, [items, displayCategories]);

  // Alternate frames map: maps cell ids to {item, frame} pairs for non-primary frames
  const frameCellMap = useMemo(() => {
    const map: Record<string, Array<{ item: Item; frame: ItemFrame }>> = {};
    for (const cat of displayCategories) {
      for (const alt of ALTITUDES) {
        map[cellId(cat as ItemLanguageType, alt)] = [];
      }
    }
    for (const item of items) {
      if (!item.frames?.length) continue;
      for (const frame of item.frames) {
        const cat = frame.frameType as string;
        const alt = Number(frame.altitudeLevel);
        if (cat && alt && displayCategories.includes(cat) && (ALTITUDES as readonly number[]).includes(alt as typeof ALTITUDES[number])) {
          // Only include if this frame's cell differs from the item's primary cell
          if (cat !== item.itemCategory || alt !== Number(item.altitudeLevel)) {
            map[cellId(cat as ItemLanguageType, alt)]?.push({ item: item as Item, frame });
          }
        }
      }
    }
    return map;
  }, [items, displayCategories]);

  const activeItem = activeId ? items.find(i => i.id === activeId) as Item | undefined : undefined;

  const handleDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  };

  const handleDragOver = (e: { over: { id: string | number } | null }) => {
    setOverId(e.over ? String(e.over.id) : null);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    setOverId(null);
    const { active, over } = e;
    if (!over) return;

    const target = parseCellId(String(over.id));
    if (!target) return;

    const item = items.find(i => i.id === active.id) as Item | undefined;
    if (!item) return;
    if (item.itemCategory === target.cat && item.altitudeLevel === target.alt) return;

    const hasWorkSize = !!item.altitudeScopingInputs?.workSize;
    const preserveEffort =
      item.effortSource === 'planner_override' || item.effortSource === 'explicit';
    const inferred =
      !hasWorkSize && !preserveEffort
        ? effortFieldsFromAltitudeLevel(target.alt, getItemEffortBandHours())
        : null;

    updateItem(item.id, {
      itemCategory: target.cat,
      altitudeLevel: target.alt as Item['altitudeLevel'],
      ...(inferred ?? {}),
    });
  };

  const totalItems = items.length;
  const typedItems = items.filter(i => i.itemCategory && i.altitudeLevel).length;
  const untypedItems = totalItems - typedItems;
  const [showGuide, setShowGuide] = useState(false);

  const toggleAltitudeSection = (alt: number) => {
    setExpandedAltitude(expandedAltitude === alt ? null : alt);
  };

  const ITEM_TYPES_GUIDE: InfoSection[] = [
    {
      id: 'axes',
      label: 'How to read this grid',
      description: 'Each cell represents a combination of Category (columns) × Altitude (rows). The cell name is the specific type label for that intersection — for example, a Solution at altitude 3 is called an "Epic", while a Solution at altitude 1 is a "Task". Drag any item chip to a new cell to change its category and/or altitude.',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)]', iconColor: 'text-[var(--text-ter)]',
    },
    {
      id: 'categories',
      label: 'Columns — Categories',
      description: 'The six columns represent the six item categories: Problem, Opportunity, Risk, Solution, Commitment, and Investment. Each represents a different way to describe an item — what the item is in terms of its nature and intent.',
      examples: ['Problem = something wrong', 'Opportunity = potential upside', 'Risk = threat to mitigate', 'Solution = work to build', 'Commitment = obligated work', 'Investment = capability building'],
      color: 'bg-[var(--surface)] border-[var(--rule)]',
    },
    {
      id: 'altitudes',
      label: 'Rows — Altitude',
      description: 'The five rows represent altitude levels 1–5, from most operational (Level 1) to most strategic (Level 5). Altitude reflects scope, time horizon, and organizational elevation — not priority or importance.',
      examples: ['Level 5 = strategic, org-wide, multi-year', 'Level 4 = programmatic, multi-team, multi-quarter', 'Level 3 = initiative, single-team, quarterly', 'Level 2 = tactical, sprint-scale', 'Level 1 = operational, days or hours'],
      color: 'bg-[var(--surface)] border-[var(--rule)]',
    },
    {
      id: 'names',
      label: 'Cell names — the 30 types',
      description: 'Each cell has a specific name that combines category and altitude into a recognizable label. These names are conventions — they help people communicate about items at the right level of abstraction without needing to specify both axes each time.',
      examples: ['Epic = Solution × Level 3', 'Strategy = Opportunity × Level 5', 'Bug = Problem × Level 1', 'Threat = Risk × Level 4', 'Action = Commitment × Level 1', 'Transformation = Investment × Level 5'],
      color: 'bg-[var(--surface)] border-[var(--rule)]',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <PageHeader
          icon={<LayoutGrid className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Comparison Grid"
          subtitle="Drag items between cells to change their category or altitude."
        />
        <div className="flex flex-wrap items-center gap-2">
          {/* Show/hide items toggle */}
          <button
            type="button"
            onClick={() => setShowItems(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border  transition-colors
              ${showItems
                ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] hover:bg-[var(--slate-dim)]'
                : 'bg-[var(--bg-raised)] text-[var(--text-ter)] border-[var(--rule)] hover:bg-[var(--surface)] hover:text-[var(--text-sec)]'}`}
            title={showItems ? 'Hide items — show cell names only' : 'Show items in cells'}
          >
            {showItems ? <List className="h-3.5 w-3.5" /> : <Tag className="h-3.5 w-3.5" />}
            {showItems ? 'Items' : 'Names only'}
          </button>
          {/* Alternate frames toggle — only meaningful when items are visible */}
          {showItems && (
            <button
              type="button"
              onClick={() => setShowAlternateFrames(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border  transition-colors
                ${showAlternateFrames
                  ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] hover:bg-[var(--slate-dim)]'
                  : 'bg-[var(--bg-raised)] text-[var(--text-ter)] border-[var(--rule)] hover:bg-[var(--surface)] hover:text-[var(--text-sec)]'}`}
              title="Show items that have an additional category in each cell — the same item can appear multiple times"
            >
              <Repeat2 className="h-3.5 w-3.5" />
              Additional categories
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            title="How does this grid work?"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] bg-[var(--bg-raised)] hover:bg-[var(--slate-dim)] border border-[var(--rule)] transition-colors"
          >
            <Info className="h-3.5 w-3.5" />
            How to read this
          </button>
        </div>
      </div>
      <InfoModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="Comparison Grid"
        subtitle="Category × Altitude = Type name"
        sections={ITEM_TYPES_GUIDE}
      />

      <div className="space-y-6">
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {/* Grid — phone: accordion by altitude; tablet+: sticky row/column headers */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            {isPhone ? (
              <div className="divide-y divide-[var(--rule)]">
                {ALTITUDES.map((alt) => {
                  const rowItemCount = displayCategories.reduce(
                    (sum, cat) => sum + (cellMap[cellId(cat as ItemLanguageType, alt)]?.length ?? 0),
                    0,
                  );
                  const isAltitudeOpen = expandedAltitude === alt;

                  return (
                    <div key={alt}>
                      <button
                        type="button"
                        onClick={() => toggleAltitudeSection(alt)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
                      >
                        <span className="text-sm font-semibold text-[var(--text-pri)]">
                          Level {alt}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-[var(--text-ter)]">
                            {rowItemCount} item{rowItemCount !== 1 ? 's' : ''}
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 text-[var(--text-ter)] transition-transform ${isAltitudeOpen ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </button>

                      {isAltitudeOpen && (
                        <div className="divide-y divide-[var(--rule)]">
                          {displayCategories.map((cat) => {
                            const id = cellId(cat as ItemLanguageType, alt);
                            const cellItems = cellMap[id] ?? [];

                            return (
                              <div key={id} className="px-3 py-2">
                                <DroppableCell
                                  id={id}
                                  category={cat as ItemLanguageType}
                                  altitude={alt}
                                  cellItems={cellItems}
                                  frameItems={frameCellMap[id] ?? []}
                                  isOver={overId === id}
                                  typeName={typeNames[cat]?.[alt] ?? ''}
                                  showItems={showItems}
                                  showAlternateFrames={showAlternateFrames}
                                  onShowMore={handleShowMore}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[960px]">
                  <thead>
                    <tr>
                      <th className="sticky left-0 top-0 z-30 w-24 px-4 py-3 text-left bg-[var(--surface)] border-b border-r border-[var(--rule)] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                        <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">Altitude</span>
                      </th>
                      {displayCategories.map(cat => {
                        const colors = categoryBadgeMap[cat] ?? { bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' };
                        const hex = categoryHexMap[cat] ?? '#94a3b8';
                        return (
                          <th
                            key={cat}
                            className={`sticky top-0 z-20 px-3 py-3 text-center bg-[var(--surface)] border-b border-r border-[var(--rule)] last:border-r-0 min-w-[160px] ${colors.bg}`}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: hex }} />
                              <span className={`text-xs font-semibold ${colors.text}`}>{categoryLabelMap[cat] ?? cat}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {ALTITUDES.map((alt, rowIdx) => (
                      <tr key={alt} className={rowIdx % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/40'}>
                        <td className="sticky left-0 z-10 px-4 py-2 border-r border-[var(--rule)] align-top bg-inherit shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]">
                          <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                            Level {alt}
                          </span>
                        </td>
                        {displayCategories.map(cat => {
                          const id = cellId(cat as ItemLanguageType, alt);
                          const cellItems = cellMap[id] ?? [];
                          return (
                            <td key={cat} className="px-2 py-2 border-r border-[var(--rule)] last:border-r-0 align-top">
                              <DroppableCell
                                id={id}
                                category={cat as ItemLanguageType}
                                altitude={alt}
                                cellItems={cellItems}
                                frameItems={frameCellMap[id] ?? []}
                                isOver={overId === id}
                                typeName={typeNames[cat]?.[alt] ?? ''}
                                showItems={showItems}
                                showAlternateFrames={showAlternateFrames}
                                onShowMore={handleShowMore}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeItem && (
              <ItemDragPreview
                item={activeItem}
                category={activeItem.itemCategory as ItemLanguageType ?? 'problem'}
              />
            )}
          </DragOverlay>
        </DndContext>

        {overflowPopover && (
          <OverflowPopover
            state={overflowPopover}
            onClose={() => setOverflowPopover(null)}
          />
        )}

        {/* Summary row */}
        <div className="flex items-center gap-6 text-sm text-[var(--text-sec)] px-1">
          <span><span className="font-medium text-[var(--text-pri)]">{totalItems}</span> total items</span>
          <span className="text-[var(--text-ter)]">·</span>
          <span><span className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">{typedItems}</span> fully typed</span>
          <span className="text-[var(--text-ter)]">·</span>
          <span>
            {untypedItems > 0 ? (
              <>
                <Link to="/prioritization/items/readiness" className="font-medium text-[var(--mustard)] hover:text-[var(--mustard)] underline">
                  {untypedItems} untyped
                </Link>
                {' '}(missing category or altitude)
              </>
            ) : (
              <span className="font-medium text-[var(--text-ter)]">0 untyped</span>
            )}
          </span>
        </div>

        {untypedItems > 0 && (
          <div className="flex items-start gap-3 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[var(--mustard)]">
              <span className="font-semibold">{untypedItems} item{untypedItems !== 1 ? 's' : ''}</span>
              {' '}have no category or altitude set and won&apos;t appear in the grid.{' '}
              <Link to="/prioritization/items/readiness" className="font-medium underline hover:text-[var(--mustard)]">
                View in Readiness &rarr;
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
